import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import PrimaryFilterHelper from '../../../components/primaryFilterHelper';
import LocationFilterDropdown from '../../../components/locationFilterDropdown';
import DateRangePicker from '../../../components/dateRangePicker';
import OverviewTab from '../components/OverviewTab';
import PerformanceTab from '../components/PerformanceTab';
import ReportTab from '../components/ReportTab';
import { EngineSkeleton } from '../components/shared';
import {
  usePromotionEngineOverview,
  usePromotionEnginePerformance
} from '../hooks/usePromotionEngine';
import type { PromotionEngineTab } from '../types';

const TABS: { key: PromotionEngineTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'performance', label: 'Performance' },
  { key: 'report', label: 'Report' }
];

export default function PromotionEngineDashboard() {
  const companyDetails = useSelector(
    (state: RootState) => state.company?.company_details as any
  );
  const isMaintenance = companyDetails?.maintenance_mode?.enabled;
  const status = companyDetails?.promotion_engine_status as
    | 'enabled'
    | 'disabled'
    | undefined;

  if (status !== 'enabled') {
    return (
      <LayoutWrapper $maintenance={!!isMaintenance}>
        <Unavailable>
          <h1>Promotion Engine</h1>
          <p>
            Promotion Engine is available on demo accounts while we finish the feature.
          </p>
        </Unavailable>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper $maintenance={!!isMaintenance}>
      <PrimaryFilterHelper>
        <ActiveDashboard />
      </PrimaryFilterHelper>
    </LayoutWrapper>
  );
}

const ActiveDashboard = () => {
  const [tab, setTab] = useState<PromotionEngineTab>('overview');
  const companyLocations = useSelector((state: RootState) => state.locations);
  const overview = usePromotionEngineOverview();
  const performance = usePromotionEnginePerformance(tab === 'performance');

  const loading = useMemo(() => {
    if (tab === 'report') return false;
    const active = tab === 'overview' ? overview : performance;
    return !active.data && (active.isLoading || active.isValidating);
  }, [tab, overview, performance]);

  const error = useMemo(() => {
    if (tab === 'report') return null;
    if (tab === 'overview') return overview.error;
    return performance.error;
  }, [tab, overview, performance]);

  return (
    <>
      <Header>
        <div>
          <h1>Promotion Engine</h1>
          <Sub>Location grain · full-price vs promo · COGS from computed cost</Sub>
        </div>
        <Controls>
          {companyLocations.length > 0 && <LocationFilterDropdown />}
          <DateRangePicker />
        </Controls>
      </Header>

      <Tabs role="tablist">
        {TABS.map((t) => (
          <Tab
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            $active={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </Tab>
        ))}
      </Tabs>

      <Content>
        {loading && <EngineSkeleton tab={tab} />}
        {!loading && error && (
          <ErrorBox>
            Unable to load Promotion Engine data.
            {error?.response?.status === 404 && ' Feature may not be enabled.'}
            {error?.response?.status === 423 && ' You may not have permission.'}
          </ErrorBox>
        )}
        {!loading && !error && tab === 'overview' && overview.data && (
          <OverviewTab data={overview.data} />
        )}
        {!loading && !error && tab === 'performance' && performance.data && (
          <PerformanceTab data={performance.data} />
        )}
        {tab === 'report' && <ReportTab />}
      </Content>
    </>
  );
};

const LayoutWrapper = styled.div<{ $maintenance: boolean }>`
  background-color: ${({ theme }) => theme.body};
  margin-top: ${({ $maintenance }) => ($maintenance ? '180px' : '125px')};
  padding: 0 32px 48px;
  min-height: calc(100vh - 125px);
  transition: all 0.25s linear 0s;

  @media (max-width: 720px) {
    padding: 0 16px 32px;
    margin-top: ${({ $maintenance }) => ($maintenance ? '150px' : '100px')};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 24px 0 12px;

  h1 {
    font-weight: 800;
    margin: 0;
    color: ${({ theme }) => theme.text};
  }
`;

const Sub = styled.div`
  margin-top: 4px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.08)'};
  margin-bottom: 18px;
`;

const Tab = styled.button<{ $active: boolean }>`
  border: 0;
  background: none;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  color: ${({ $active, theme }) => ($active ? theme.text : theme.text_muted || theme.text)};
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.primary || '#0d8a6a' : 'transparent')};
`;

const Content = styled.div`
  min-height: 240px;
`;

const ErrorBox = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(225, 29, 72, 0.25);
  background: rgba(225, 29, 72, 0.06);
  color: ${({ theme }) => theme.text};
`;

const Unavailable = styled.div`
  padding: 48px 16px;
  max-width: 560px;
  h1 {
    font-weight: 800;
    color: ${({ theme }) => theme.text};
  }
  p {
    color: ${({ theme }) => theme.text_secondary || theme.text};
  }
`;
